/**
 * Permission types and interfaces for Blackbird EMR
 * Defines permission categories, scopes, and operations
 * 
 * @module types/permissions
 */

import type { FeatureFlags } from '../constants/features';
import type { PlanId, SubscriptionStatus } from '../dtos/billing/subscription.dto';
import type { SandboxStatusDto } from '../dtos/demo/sandbox.dto';

/**
 * Raw permission string format
 * 
 * Structure: `{resource}:{category}:{subject}:{action}`
 * Examples:
 *   - patientmr:phi:visit:read
 *   - patientmr:phi:insurance:update
 *   - billing:claims:create
 *   - admin:users:manage
 */
export type PermissionString = string & { readonly __brand: 'PermissionString' };

/**
 * Permission categories that organize related permissions
 */
export type PermissionCategory = 'phi' | 'pii' | 'nonPii' | 'admin' | 'general';

/**
 * Organized permission structure by category
 */
export interface PermissionsByCategory {
  phi: PermissionString[];
  pii: PermissionString[];
  nonPii: PermissionString[];
  admin: PermissionString[];
  general: PermissionString[];
}

/**
 * Permission set returned from backend /users/me/permissions endpoint
 */
export interface PermissionObject {
  permissions: PermissionString[];
  permissionsByCategory: PermissionsByCategory;
}

/** A legal document version the user still has to accept (Phase 5, ACC-02). */
export type LegalDocType = 'tos' | 'privacy';
export interface PendingLegalDoc {
  docType: LegalDocType;
  version: string;
}

/**
 * Tenant BAA state (Phase 5, ACC-03/04).
 * accepted: current version signed. grace: an older version is signed and the new one is
 * inside its 30-day grace window. expired: grace is over, PHI writes are blocked.
 * none: no BAA signed yet (full block).
 */
export type BaaStatus = 'accepted' | 'grace' | 'expired' | 'none';

/**
 * Phase 13: the tenant's subscription as the UI uses it (normalized from the `subscription`
 * block on GET /users/me/permissions). Absent when an older backend omits the block.
 */
export interface AccountSubscription {
  plan: PlanId;
  status: SubscriptionStatus;
  trialDaysLeft: number | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  exportUntil: string | null;
  billingEnabled: boolean;
  /** A card is on file (Stripe subscription exists); false on an older backend. */
  hasPaymentMethod?: boolean;
  /** Patient cap usage; pct is 0–100, computed from used/limit when possible. */
  cap: { used: number; limit: number | null; pct: number | null };
}

/** What the tenant's plan includes. A missing key means not included. */
export interface PlanEntitlements {
  familyPortal?: boolean;
  offlineMode?: boolean;
}

/** Agency timezone used when nothing else is known. */
export const DEFAULT_AGENCY_TIMEZONE = 'America/Chicago';

/**
 * Extended User interface with permission support
 */
export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  tenantId: string;
  providerType?: string;
  userType?: string; // From custom:user_type (EMPLOYEE or GUARDIAN)
  isProvider?: boolean;
  groups?: string[]; // Cognito user groups
  baaAccepted?: boolean;
  baaSignerRole?: string | null;
  /** Phase 5: BAA version state; derived from baaAccepted when the backend omits it. */
  baaStatus?: BaaStatus;
  /** ISO date the grace period ends (baaStatus 'grace'), else null. */
  baaGraceEndsAt?: string | null;
  currentBaaVersion?: string | null;
  /** Phase 5: ToS / Privacy versions the user must accept before using the app. */
  pendingLegal?: PendingLegalDoc[];
  /** IANA timezone of the agency (from /users/me/permissions agency.timezone). */
  agencyTimezone?: string;
  /** R-177: the agency collects EVV visit data for PDN (EVV-optional in Texas). */
  evvCollectPdnVisits?: boolean;
  /** ACC-05 MFA enrollment gate (from /users/me/permissions). required is false while enforcement is off. */
  mfa?: { required: boolean; enrolled: boolean | null };
  /** Phase 3 tenant feature flags (from /users/me/permissions), resolved against FEATURE_DEFAULTS. */
  features?: FeatureFlags;
  /** Phase 13 subscription state; undefined when the backend does not send it. */
  subscription?: AccountSubscription;
  /** Plan entitlements (GET /users/me/permissions, from billing/plans.js). offlineMode: Core and above (Phase 16A). */
  entitlements?: PlanEntitlements;
  /** Demo sandbox (GET /users/me/permissions `sandbox`); null for a normal tenant. */
  sandbox?: SandboxStatusDto | null;

  // Permission state
  permissions?: PermissionString[];
  permissionsByCategory?: PermissionsByCategory;
}

/**
 * AuthContext shape with permission methods
 */
export interface AuthContextType {
  user: AuthUser | null;
  setUser: React.Dispatch<React.SetStateAction<AuthUser | null>>;
  isLoading: boolean;
  error: string | null;
  /** The signed-in user has no user/tenant (403 ACCOUNT_SETUP_INCOMPLETE: sign-up did not finish). */
  accountSetupIncomplete: boolean;
  loadPermissions: () => Promise<void>;
  /** Re-fetch /users/me/permissions (after accepting legal documents or the BAA). */
  refreshPermissions: () => Promise<void>;
  /** Agency timezone, DEFAULT_AGENCY_TIMEZONE until permissions load. */
  agencyTimezone: string;
  clearPermissions: () => void;
  permissionsLoaded: boolean;
  setPermissionsLoaded: (loaded: boolean) => void;
}

/**
 * Common operations mapped to backend permission codes
 * Used by useCanPerform hook for high-level permission checks
 */
export interface OperationPermissionMap {
  // Visit operations
  'create:visit': PermissionString;
  'read:visit': PermissionString;
  'update:visit': PermissionString;
  'delete:visit': PermissionString;

  // Insurance operations
  'read:insurance': PermissionString;
  'create:insurance': PermissionString;
  'update:insurance': PermissionString;
  'delete:insurance': PermissionString;

  // Authorization/Prior Auth operations
  'create:authorization': PermissionString;
  'approve:authorization': PermissionString;
  'deny:authorization': PermissionString;

  // Billing operations
  'read:claims': PermissionString;
  'create:claims': PermissionString;
  'approve:claims': PermissionString;
  'delete:claims': PermissionString;

  // Assessment operations
  'create:assessment': PermissionString;
  'read:assessment': PermissionString;
  'update:assessment': PermissionString;

  // Care Plan operations
  'create:careplan': PermissionString;
  'read:careplan': PermissionString;
  'update:careplan': PermissionString;

  // Admin operations
  'manage:users': PermissionString;
  'manage:roles': PermissionString;
  'view:audit:log': PermissionString;
  'download:audit:log': PermissionString;
  'export:patient': PermissionString;
  'export:agency': PermissionString;
  'manage:subscription': PermissionString;

  // Admin user operations
  'read:users:pii': PermissionString;
  'create:users:pii': PermissionString;
  'update:users:pii': PermissionString;
  /** Invite resend/revoke (Phase 5) */
  'update:users:nonpii': PermissionString;

  // Admin agency operations
  'read:agencies': PermissionString;
  'create:agencies': PermissionString;
  'update:agencies': PermissionString;

  // Admin config operations
  'read:config': PermissionString;
  'write:config': PermissionString;

  // Family Portal staff operations (Phase 12)
  'manage:guardians': PermissionString;
  'view:guardians': PermissionString;
  'approve:guardians': PermissionString;

  // Admin patient operations
  'read:patients:nonphi': PermissionString;
  'create:patients:nonphi': PermissionString | PermissionString[];
  'create:patients:phi': PermissionString | PermissionString[];
  'update:patients:nonphi': PermissionString | PermissionString[];
  'update:patients:phi': PermissionString | PermissionString[];
  'read:patients:familyportal': PermissionString | PermissionString[];
  'write:patients:familyportal': PermissionString | PermissionString[];
  'read:patients:phi:all': PermissionString;
  /** Admit a pending_admission patient (Phase 6, SOC-05): admin:patients:admit */
  'admit:patients': PermissionString;
  /** Discharge / readmit / correct an INACTIVE chart (Phase 6, DSC-02/07): admin:patients:discharge */
  'discharge:patients': PermissionString;

  // Family portal guardian reads (Phase 12)
  'read:portal': PermissionString;

  // Discharge / transfer summaries (Phase 16E, DSC-22)
  'read:dischargesummary': PermissionString;
  'update:dischargesummary': PermissionString;
  'sign:dischargesummary': PermissionString;
  'qa:dischargesummary': PermissionString;
  'send:dischargesummary': PermissionString;

  // Critical notifications (Phase 16F): every staff role holds the self permissions
  'read:notifications': PermissionString;
  'update:notifications': PermissionString;
  'manage:notificationsettings': PermissionString;
}

/**
 * Operation keys supported by useCanPerform
 */
export type OperationKey = keyof OperationPermissionMap;

/**
 * Helper function to create branded PermissionString
 * @internal - Not exported to users; backend is authority on permission strings
 */
export function createPermissionString(value: string): PermissionString {
  return value as PermissionString;
}
